import * as React from 'react';
import { memo } from 'react';

import styled from 'styled-components';

/**
 * Знак карьерного маршрута (ТЗ 0069).
 *
 * Заменяет эмодзи, которыми был размечен весь карьерный контур (🎓 📄 💼 🏅 🔒
 * 📎 👁 ★ ✓ 👉). Эмодзи — это не только визуальный разнобой: они рисуются
 * шрифтом системы и выглядят на macOS, Windows и Android тремя разными
 * картинками, а в `CareerLessonRow` звёздочка стояла БЕЗ `aria-hidden` и С
 * `aria-label`, то есть читалка произносила название символа вместо подписи.
 *
 * 🔴 Библиотеку иконок не тащим — это записанное решение, а не осторожность.
 * `lucide-react` 1.x уже ломал сборку ERP на webpack 4 (см. TrackIcon,
 * MedalIcon), а `@ant-design/icons` лежит в `node_modules` ТРАНЗИТИВНО от
 * antd и в `package.json` не объявлен: импорт необъявленной зависимости
 * сломается на первом же изменении hoisting в локфайле.
 *
 * Приём тот же, что у `TrackIcon`: пути в координатах 24×24, обводка
 * `currentColor` (контур одинаково читается и в 16px рядом с текстом, и в 40px
 * на карточке), декоративность по умолчанию.
 */
export type CareerIconName =
  | 'goal'
  | 'learning'
  | 'portfolio'
  | 'cv'
  | 'applications'
  | 'interview'
  | 'offer'
  | 'lock'
  | 'check'
  | 'eye'
  | 'arrowRight'
  | 'paperclip'
  | 'star'
  | 'starFilled'
  | 'externalLink'
  | 'calendar'
  | 'clock'
  | 'warning'
  | 'bookmark'
  | 'list'
  | 'compass'
  | 'skills'
  | 'briefcase'
  | 'search'
  | 'profile'
  | 'medal'
  | 'play'
  | 'quiz'
  | 'clipboard'
  | 'chevronDown'
  | 'menu'
  | 'close';

export interface ICareerIcon {
  name: CareerIconName;
  size?: number;
  /**
   * Доступное имя. Задавать ТОЛЬКО когда значок стоит без текстовой подписи
   * рядом: иначе читалка произнесёт одно и то же дважды.
   */
  title?: string;
  className?: string;
}

/** Ключ → фигуры в координатах 24×24. */
/**
 * Пути знака в координатах 24×24.
 *
 * Экспортируется намеренно: карта навыков рисует свои узлы прямым SVG, и
 * вложить туда React-компонент `CareerIcon` нельзя без `foreignObject`, а тот
 * ломает и масштабирование, и печать. Рисовать те же значки второй раз —
 * гарантия, что через месяц на карте и в списке будут разные галочки.
 */
export const SHAPES: Record<CareerIconName, string[]> = {
  // мишень — карьерная цель
  goal: [
    'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z',
    'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
    'M12 12h.01',
  ],
  // конфедератка — обучение
  learning: [
    'M12 4 2.5 8.5 12 13l9.5-4.5L12 4z',
    'M6.5 10.7V15c0 1.4 2.5 2.6 5.5 2.6s5.5-1.2 5.5-2.6v-4.3',
    'M21.5 8.5V14',
  ],
  // папка с работами — портфолио
  portfolio: [
    'M4 8h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z',
    'M9 8V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2',
    'M3 13h18',
  ],
  // лист с текстом — резюме
  cv: [
    'M6 3h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z',
    'M14 3v4h4',
    'M8.5 12h7',
    'M8.5 15.5h7',
    'M8.5 19h4',
  ],
  // бумажный самолётик — отклики
  applications: ['M21 3 10.5 13.5', 'M21 3l-6.5 18-4-8-8-4L21 3z'],
  // двое за столом — интервью
  interview: [
    'M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    'M2.5 20c0-3 2.5-5.2 5.5-5.2s5.5 2.2 5.5 5.2',
    'M16.5 9.5h5v5l-2 -1.2h-3z',
  ],
  // рукопожатие/награда — оффер
  offer: ['M12 3.5a5 5 0 1 0 0 10 5 5 0 0 0 0-10z', 'M8.5 13 7 21l5-2.5L17 21l-1.5-8'],
  // замок — заблокировано
  lock: [
    'M6 10.5h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1z',
    'M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3',
  ],
  check: ['M4.5 12.5 9.5 17.5 19.5 6.5'],
  eye: [
    'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z',
    'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z',
  ],
  arrowRight: ['M4 12h15', 'M13.5 6.5 20 12l-6.5 5.5'],
  paperclip: [
    'M20 11.5 12.2 19.3a5 5 0 0 1-7-7l8-8a3.4 3.4 0 0 1 4.8 4.8l-8 8a1.8 1.8 0 0 1-2.5-2.5l7.3-7.3',
  ],
  star: ['M12 3.5l2.7 5.6 6.1.8-4.5 4.3 1.2 6.1L12 17.4l-5.5 2.9 1.2-6.1L3.2 9.9l6.1-.8L12 3.5z'],
  starFilled: [
    'M12 3.5l2.7 5.6 6.1.8-4.5 4.3 1.2 6.1L12 17.4l-5.5 2.9 1.2-6.1L3.2 9.9l6.1-.8L12 3.5z',
  ],
  externalLink: [
    'M14 4h6v6',
    'M20 4l-8.5 8.5',
    'M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
  ],
  calendar: [
    'M4 6h16a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z',
    'M3 10.5h18',
    'M8 3v4',
    'M16 3v4',
  ],
  clock: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z', 'M12 7v5.5l3.5 2'],
  warning: ['M12 3.5 21.5 20H2.5L12 3.5z', 'M12 10v4.5', 'M12 17.5h.01'],
  bookmark: ['M6.5 3.5h11a1 1 0 0 1 1 1v16l-6.5-4-6.5 4v-16a1 1 0 0 1 1-1z'],
  // строки со сносками — режим списка.
  //
  // Своя фигура, а не `cv` или `portfolio`: те на соседних экранах раздела уже
  // означают «резюме» и «портфолио», и занять любую значило бы столкнуть два
  // смысла в одном значке.
  list: ['M9 6h11', 'M9 12h11', 'M9 18h11', 'M4.5 6h.01', 'M4.5 12h.01', 'M4.5 18h.01'],
  // ── Доработка 0071 (15.09): знаки разделов меню и состава урока ────────
  // компас — «Mój plan»: куда я иду
  compass: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z', 'M15.5 8.5 13.5 13.5 8.5 15.5 10.5 10.5z'],
  // три связанных узла — карта навыков
  skills: [
    'M6 6.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    'M18 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    'M8 21.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    'M7.3 6 16.5 8',
    'M6.4 6.4 7.7 17.5',
    'M16.7 9.5 9.3 18.1',
  ],
  // портфель — профессии
  briefcase: [
    'M4 7.5h16a1 1 0 0 1 1 1V19a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8.5a1 1 0 0 1 1-1z',
    'M8.5 7.5V5.5a1.5 1.5 0 0 1 1.5-1.5h4a1.5 1.5 0 0 1 1.5 1.5v2',
    'M3 12.5c5.5 2.3 12.5 2.3 18 0',
  ],
  // лупа — вакансии
  search: ['M10.5 4a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13z', 'M15.5 15.5 20.5 20.5'],
  // человек — профиль и CV
  profile: ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M4.5 21c0-4 3.4-6.5 7.5-6.5s7.5 2.5 7.5 6.5'],
  // медаль на ленте — достижения
  medal: [
    'M8 3h3l1.5 5M16 3h-3',
    'M12 21a6 6 0 1 0 0-12 6 6 0 0 0 0 12z',
    'M12 12.5l1 2 2.2.3-1.6 1.5.4 2.2-2-1-2 1 .4-2.2-1.6-1.5 2.2-.3 1-2z',
  ],
  // треугольник — видео
  play: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z', 'M10 8.5v7l6-3.5-6-3.5z'],
  // список с галочками — тест «Sprawdź się»
  quiz: [
    'M4 6.5l1.5 1.5 2.5-3',
    'M4 13l1.5 1.5 2.5-3',
    'M11 6.5h9',
    'M11 13h9',
    'M4 19.5h.01',
    'M11 19.5h9',
  ],
  // планшет с листом — анкета, упражнение
  clipboard: [
    'M8 4.5H6a1 1 0 0 0-1 1V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V5.5a1 1 0 0 0-1-1h-2',
    'M9 3h6v3H9z',
    'M8.5 11h7',
    'M8.5 15h5',
  ],
  chevronDown: ['M6 9.5l6 6 6-6'],
  menu: ['M4 6.5h16', 'M4 12h16', 'M4 17.5h16'],
  close: ['M6 6l12 12', 'M18 6L6 18'],
};

/** Знаки, у которых форма несёт смысл заливкой, а не контуром. */
const FILLED: CareerIconName[] = ['starFilled'];

const Svg = styled.svg`
  flex: none;
  display: inline-block;
  vertical-align: middle;
`;

export const CareerIcon = memo(({ name, size = 20, title, className }: ICareerIcon) => {
  const filled = FILLED.indexOf(name) !== -1;

  return (
    <Svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      // Значок почти всегда стоит рядом с подписью и обязан молчать. Голос он
      // получает только когда `title` задан явно.
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      aria-label={title}>
      {SHAPES[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </Svg>
  );
});
